import React, { useRef, useState } from "react";
import classNames from "classnames";
import Icon from "../../theme/untitled/icons/Icon";
import Tooltip from "../../theme/untitled/Tooltip/Tooltip";
import { getUploadResourceUrl } from "../../../../helpers/url";
import { parseGeneralError } from "./errorHelpers";
import styles from "./BrandingPage.module.scss";

const MAX_FILE_KB = 512;
const ACCEPTED_TYPES = ["image/png", "image/jpeg"];
const ACCEPT_ATTR = "image/png, image/jpeg";

const formatMb = (bytes) => (bytes / (1024 * 1024)).toFixed(1);

const UploadTile = ({
  variant, // "icon" | "logo"
  title,
  tooltip,
  description,
  value,
  unsetMessage,
  onChange,
  uploading,
  disabled,
  uploadError,
}) => {
  const [clientError, setClientError] = useState(null);
  const inputRef = useRef(null);

  const openPicker = () => inputRef.current?.click();

  const onSelectFile = (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;

    if (!ACCEPTED_TYPES.includes(file.type)) {
      setClientError(`${file.name} isn't a supported format. Upload a PNG or JPEG.`);
      return;
    }
    if (file.size > MAX_FILE_KB * 1024) {
      setClientError(`${file.name} is ${formatMb(file.size)} MB. Upload a file under 512 KB.`);
      return;
    }
    setClientError(null);
    onChange(file);
  };

  const onRemove = () => {
    setClientError(null);
    onChange(null);
  };

  const serverErrorMessage = uploadError
    ? parseGeneralError(uploadError, "Upload failed — the connection dropped. Nothing was changed.")
    : null;
  const errorMessage = clientError || serverErrorMessage;

  const previewUrl = value ? getUploadResourceUrl(value) : null;
  const fileName = value instanceof File ? value.name : null;

  let tile;
  if (uploading) {
    tile = (
      <div className={classNames(styles.tile, styles[`tile--${variant}`], styles["tile--busy"])}>
        <span className={styles.tileSpinner} aria-hidden="true" />
      </div>
    );
  } else if (errorMessage) {
    tile = (
      <button
        type="button"
        className={classNames(
          styles.tile,
          styles[`tile--${variant}`],
          styles["tile--add"],
          styles["tile--error"]
        )}
        onClick={openPicker}
        disabled={disabled}
      >
        <Icon name="AlertCircle" size={18} />
        <span className={styles.tileAddCaption}>{value ? "Try again" : "Retry"}</span>
      </button>
    );
  } else if (previewUrl) {
    tile = (
      <div className={classNames(styles.tile, styles[`tile--${variant}`])}>
        <img className={styles.tileImage} src={previewUrl} alt="" />
      </div>
    );
  } else {
    tile = (
      <button
        type="button"
        className={classNames(styles.tile, styles[`tile--${variant}`], styles["tile--add"])}
        onClick={openPicker}
        disabled={disabled}
      >
        <Icon name="Plus" size={18} />
        <span className={styles.tileAddCaption}>{variant === "logo" ? "Add logo" : "Add"}</span>
      </button>
    );
  }

  return (
    <div className={styles.row}>
      <div className={styles.rowLabel}>
        <div className={styles.rowTitle}>
          {title}
          {tooltip && (
            <Tooltip content={tooltip}>
              <span className={styles.rowTitleHelp} tabIndex={0}>
                <Icon name="HelpCircle" size={14} />
              </span>
            </Tooltip>
          )}
        </div>
        <div className={styles.rowDescription}>{description}</div>
      </div>

      <div className={styles.control}>
        {tile}
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT_ATTR}
          onChange={onSelectFile}
          className={styles.hiddenInput}
        />

        {uploading && <span className={styles.meta}>Uploading {fileName || "file"}…</span>}

        {!uploading && errorMessage && (
          <div className={styles.fieldError}>
            <Icon name="AlertCircle" size={14} className={styles.fieldErrorIcon} />
            <span>{errorMessage}</span>
          </div>
        )}

        {!uploading && !errorMessage && previewUrl && (
          <div className={styles.tileActs}>
            <button type="button" className={styles.linkBtn} onClick={openPicker} disabled={disabled}>
              <Icon name="UploadCloud01" size={14} />
              Replace
            </button>
            {!disabled && (
              <button
                type="button"
                className={classNames(styles.linkBtn, styles["linkBtn--destructive"])}
                onClick={onRemove}
              >
                <Icon name="Trash01" size={14} />
                Remove
              </button>
            )}
          </div>
        )}

        {!uploading && !errorMessage && !previewUrl && unsetMessage && (
          <span className={styles.meta}>{unsetMessage}</span>
        )}
      </div>
    </div>
  );
};

export default UploadTile;
